import md5 from 'md5';
import StorageUtil from './storage-util';
import {SystemConst} from './constant';
import UrlUtil from './url-config';

const REPORT_PATH = '/v1/shop/authDebug/noAuth/report';
const MINI_RESTORE_APPLIED_FLAG = 'ysdhMiniRestoreApplied';
const DEDUPE_WINDOW = 60 * 1000;
const MAX_EXTRA_LENGTH = 1200;
const sentMap = {};
const sessionId = createSessionId();

function safeRun(fn, fallback) {
    try {
        return fn();
    } catch (err) {
        return fallback;
    }
}

function createSessionId() {
    return `h5_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function limit(value, maxLength) {
    if (value === undefined || value === null) return value;
    const str = String(value);
    return str.length > maxLength ? str.substring(0, maxLength) : str;
}

function safeGetItem(key, isSession) {
    return safeRun(() => StorageUtil.getItem(key, isSession), null);
}

function safeRawLocalGet(key) {
    return safeRun(() => localStorage.getItem(key), null);
}

function getUserAgent() {
    return safeRun(() => window.navigator.userAgent, '');
}

function getLocationHref() {
    return safeRun(() => window.location.href, '');
}

function getLocationSearch() {
    return safeRun(() => window.location.search, '');
}

function getLocationPath() {
    return safeRun(() => window.location.pathname, '');
}

function getSearchParam(name) {
    const search = getLocationSearch();
    if (!search) return null;
    return safeRun(() => {
        if (typeof URLSearchParams !== 'undefined') {
            const params = new URLSearchParams(search);
            return params.has(name) ? params.get(name) : null;
        }
        const reg = new RegExp(`(^|&)${name}=([^&]*)(&|$)`);
        const result = search.replace(/^\?/, '').match(reg);
        return result ? decodeURIComponent(result[2]) : null;
    }, null);
}

function hasMiniRestoreAppliedFlag() {
    return safeRun(() => sessionStorage.getItem(MINI_RESTORE_APPLIED_FLAG) === '1', false);
}

function hasMiniSignal() {
    const ua = getUserAgent();
    return safeRun(() => window.__wxjs_environment === 'miniprogram', false)
        || safeRun(() => window.__ysdh_is_mini_program === true, false)
        || /miniprogram/i.test(ua)
        || getSearchParam('mp_restore') === '1'
        || getSearchParam('mp_auth_synced') === '1'
        || getSearchParam('mp_restore_failed') !== null
        || hasMiniRestoreAppliedFlag();
}

function getPlatform() {
    const ua = getUserAgent();
    if (hasMiniSignal()) return 'MINI_WEBVIEW';
    if (ua.indexOf('YunShlApp') > -1) return 'APP_WEBVIEW';
    if (ua.indexOf('MicroMessenger') > -1) return 'WECHAT_H5';
    if (/(iPhone|iPod|Android|ios)/i.test(ua)) return 'MOBILE_BROWSER';
    return 'PC';
}

function getCompanyId(extra, ysUser) {
    return extra.company_id_
        || extra.companyId
        || getSearchParam('companyId')
        || (ysUser && ysUser.company_id_)
        || safeGetItem(SystemConst.companyId)
        || safeGetItem(SystemConst.platformCompanyId);
}

function safeHash(value) {
    if (!value) return null;
    return safeRun(() => md5(String(value)), null);
}

function sanitizeExtra(extra) {
    const safeExtra = {};
    const source = extra || {};
    Object.keys(source).forEach(key => {
        if (/token|password|auth.?code/i.test(key)) return;
        const value = source[key];
        if (value === undefined || value === null) return;
        if (typeof value === 'object') {
            safeExtra[key] = safeRun(() => JSON.parse(JSON.stringify(value)), '[object]');
        } else {
            safeExtra[key] = limit(value, 255);
        }
    });
    return safeExtra;
}

function stringifyExtra(extra) {
    return safeRun(() => limit(JSON.stringify(sanitizeExtra(extra)), MAX_EXTRA_LENGTH), '{}');
}

export function buildAuthDebugPayload(eventType, extra) {
    const ext = extra || {};
    const ysUser = safeGetItem(SystemConst.mShop_user);
    const rawToken = safeRawLocalGet(SystemConst.token);
    const token = safeGetItem(SystemConst.token);
    const companyId = getCompanyId(ext, ysUser);

    return {
        company_id_: companyId ? Number(companyId) : undefined,
        company_name_: limit(ext.company_name_ || (ysUser && ysUser.company_name_) || safeGetItem(SystemConst.companyName), 128),
        client_id_: ext.client_id_ || (ysUser && ysUser.client_id_),
        client_code_: limit(ext.client_code_ || (ysUser && ysUser.client_code_), 64),
        client_name_: limit(ext.client_name_ || (ysUser && ysUser.client_name_), 128),
        user_id_: ext.user_id_ || (ysUser && ysUser.id_),
        login_: limit(ext.login_ || (ysUser && ysUser.login_), 128),
        phone_: limit(ext.phone_ || (ysUser && (ysUser.phone_ || ysUser.name_)), 64),
        event_type_: limit(eventType, 64),
        event_source_: 'H5',
        platform_: getPlatform(),
        token_type_: getPlatform() === 'APP_WEBVIEW' ? 'APP' : 'WEBAPP',
        app_source_: getPlatform(),
        http_status_: ext.http_status_,
        status_code_: limit(ext.status_code_, 32),
        request_url_: limit(ext.request_url_ || getLocationHref(), 512),
        request_method_: limit(ext.request_method_, 16),
        token_hash_: safeHash(token || rawToken),
        auth_code_hash_: safeHash(ext.auth_code_hash_source_),
        token_exists_: rawToken || token ? 1 : 0,
        ys_user_exists_: ysUser ? 1 : 0,
        mini_auth_exists_: ext.mini_auth_exists_,
        remove_source_: limit(ext.remove_source_, 64),
        reason_: limit(ext.reason_, 255),
        device_id_: limit(ext.device_id_, 128),
        session_id_: sessionId,
        trace_id_: limit(ext.trace_id_, 128),
        user_agent_: limit(getUserAgent(), 512),
        h5_version_: limit(ext.h5_version_, 64),
        mini_version_: limit(ext.mini_version_, 64),
        app_version_: limit(ext.app_version_, 64),
        extra_json_: stringifyExtra({
            ...ext,
            page_url_: getLocationHref(),
            route_: getLocationPath(),
            report_time_: Date.now()
        })
    };
}

function shouldSkipByDedupe(key) {
    if (!key) return false;
    const now = Date.now();
    const last = sentMap[key];
    if (last && now - last < DEDUPE_WINDOW) {
        return true;
    }
    sentMap[key] = now;
    return false;
}

function removeUndefined(payload) {
    const result = {};
    Object.keys(payload || {}).forEach(key => {
        if (payload[key] !== undefined && payload[key] !== null) {
            result[key] = payload[key];
        }
    });
    return result;
}

function sendPayload(payload) {
    const url = `${UrlUtil.getAPI()}${REPORT_PATH}`;
    const body = JSON.stringify(removeUndefined(payload));
    const nav = safeRun(() => window.navigator, {});

    if (nav && typeof nav.sendBeacon === 'function') {
        const beaconSent = safeRun(() => nav.sendBeacon(url, body), false);
        if (beaconSent) return;
    }

    if (typeof fetch === 'function') {
        safeRun(() => {
            fetch(url, {
                method: 'POST',
                headers: {'Content-Type': 'application/json;charset=utf-8'},
                body,
                keepalive: true
            }).catch(() => {});
        });
    }
}

export function safeAuthDebugReport(eventType, extra = {}, options = {}) {
    try {
        if (!options.force && !hasMiniSignal()) return false;
        const dedupeKey = options.dedupeKey || `${eventType}:${extra.reason_ || extra.reason || ''}:${extra.request_url_ || ''}`;
        if (shouldSkipByDedupe(dedupeKey)) return false;
        sendPayload(buildAuthDebugPayload(eventType, extra));
        return true;
    } catch (err) {
        return false;
    }
}

export function reportH5StorageState(reason, extra = {}) {
    return safeAuthDebugReport('H5_STORAGE_STATE', {
        ...extra,
        reason_: reason
    }, {dedupeKey: `H5_STORAGE_STATE:${reason}`});
}

export function reportH5MiniAuthState(reason, extra = {}) {
    return safeAuthDebugReport('MINI_AUTH_STATE', {
        ...extra,
        reason_: reason
    }, {dedupeKey: `MINI_AUTH_STATE:${reason}:${extra.company_id_ || extra.companyId || ''}`});
}

export function reportApi401(err, requestUrl, requestMethod) {
    return safeAuthDebugReport('API_401', {
        http_status_: err && err.state,
        status_code_: err && err['status-code'],
        request_url_: requestUrl,
        request_method_: requestMethod,
        reason_: 'route_fetch_401'
    }, {dedupeKey: `API_401:${requestUrl}:${err && err['status-code']}`});
}

export function reportH5Logout(reason, extra = {}) {
    return safeAuthDebugReport('H5_TO_LOGOUT', {
        ...extra,
        reason_: reason
    }, {dedupeKey: `H5_TO_LOGOUT:${reason}:${extra.company_id_ || extra.companyId || ''}`});
}

export const AuthDebugReporterTest = {
    hasMiniSignal,
    buildAuthDebugPayload,
    safeHash,
    sentMap
};



// WEBPACK FOOTER //
// ./src/utils/auth-debug-reporter.js